import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { Observable } from "rxjs";
import { Media } from "../../../models/api/media";

@Injectable({
  providedIn: "root",
})
export class MediaService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  private PREDEFINDE_GATE_IMAGES = ["gate_outside", "gate_inside", "motor", "control", "suspension", "name_plate"];

  /**
   * Uploads a media file to the server associated with a specific entity.
   *
   * @param entityType - The type of entity associated with the media (e.g., GATE_SYSTEM).
   * @param entityId - The ID of the entity to which the media belongs.
   * @param mediaType - The category of media being uploaded (e.g., GATE_IMAGES).
   * @param file - The media file to be uploaded. If null, only the label will be sent.
   * @param label - A label providing additional information about the media.
   * @returns An observable that emits an array of media objects upon successful upload.
   */

  public uploadMedia(
    enitityType: string,
    entityId: string,
    mediaType: string,
    file: File | null,
    label: string
  ): Observable<Media[]> {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }

    formData.append("file", JSON.stringify({ label }));

    return this.http
      .post<
        Media[]
      >(`${environment.baseUrl}/media?entityType=${enitityType}&entityId=${entityId}&mediaType=${mediaType}`, formData)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  /**
   * Retrieves media files associated with a specific entity from the server.
   *
   * @param entityType - The type of entity to which the media belongs (e.g., GATE_SYSTEM).
   * @param entityId - The ID of the entity associated with the media.
   * @param mediaType - The category of media being retrieved (e.g., GATE_IMAGES).
   * @returns An observable that emits an array of media objects upon successful retrieval.
   */
  public getMedia(entityType: string, entityId: string, mediaType: string): Observable<Media[]> {
    return this.http
      .get<Media[]>(`${environment.baseUrl}/media?entityType=${entityType}&entityId=${entityId}&mediaType=${mediaType}`)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  /**
   * Edits an existing media file on the server.
   *
   * @param mediaId - The ID of the media to be edited.
   * @param file - The new media file to replace the existing one.
   * @param label - A label providing additional information about the media.
   * @returns An observable that emits the updated media object upon successful edit.
   */

  public editMedia(mediaId: string, file: File, label: string): Observable<Media> {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }

    formData.append("file", JSON.stringify({ label }));

    return this.http
      .put<Media>(`${environment.baseUrl}/media/${mediaId}`, formData)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  /**
   * Deletes a media file from the server.
   *
   * @param mediaId - The ID of the media to be deleted.
   * @returns An observable that emits a successful response upon deletion.
   */
  public deleteMedia(mediaId: string) {
    return this.http.delete(`${environment.baseUrl}/media/${mediaId}`).pipe(this.apiErrorHandler.handle("file"));
  }

  /**
   * Determines the available image types that can be selected based on existing types.
   * Excludes types already present in the existingTypes array, but includes the currentType
   * if provided, adding "other" as a default option.
   *
   * @param existingTypes - An array of image types that are already in use.
   * @param currentType - An optional current image type to include in the available types.
   * @returns An array of available image types that can be selected.
   */

  public getAvailableImageTypes(existingTypes: string[], currentType?: string) {
    const availableTypes = this.PREDEFINDE_GATE_IMAGES.filter((type) => !existingTypes.includes(type));
    if (currentType) {
      availableTypes.push(currentType);
    }
    availableTypes.push("other");
    return availableTypes;
  }

  /**
   * Checks if the given image label is a predefined gate image type.
   *
   * @param imageLabel - The label of the image to check.
   * @returns True if the image label is a predefined gate image type, false otherwise.
   */
  public isPredefindedGateImage(imageLabel: string) {
    return this.PREDEFINDE_GATE_IMAGES.includes(imageLabel);
  }
}
